"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { PlanetState } from "../solar/ephemeris";
import type { SimJump, SimSpeed } from "./SystemScene";
import { MOONS } from "./moons";

const SystemScene = dynamic(() => import("./SystemScene"), {
  ssr: false,
  loading: () => (
    <div className="absolute inset-0 z-0 flex items-center justify-center">
      <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/30 font-[family-name:var(--font-jetbrains-mono)]">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400/80" aria-hidden />
        computing ephemeris
      </p>
    </div>
  ),
});

const BODIES = [
  "sun",
  "mercury",
  "venus",
  "earth",
  "mars",
  "jupiter",
  "saturn",
  "uranus",
  "neptune",
] as const;

const SPEEDS: Array<{ value: SimSpeed; label: string }> = [
  { value: 0, label: "now" },
  { value: -1, label: "hold" },
  { value: 86400, label: "1 day/s" },
  { value: 604800, label: "1 wk/s" },
  { value: 2629800, label: "1 mo/s" },
];

/** Real, famous planetary geometry — jump the ephemeris there and see it.
    Dates are the well-documented event days (UTC). The model holds to
    roughly ±3000 years, so the deep past and far future are fair game.
    Note: Date.UTC passes negative (astronomical) years straight through —
    year −6 is 7 BC. */
const EVENTS: Array<{ ms: number; year: string; label: string }> = [
  { ms: Date.UTC(-6, 11, 1), year: "7 bc", label: "triple conjunction — the bethlehem star candidate" },
  { ms: Date.UTC(1186, 8, 16), year: "1186", label: "seven bodies in libra — medieval doomsday scare" },
  { ms: Date.UTC(1524, 1, 20), year: "1524", label: "five planets in pisces — europe builds arks" },
  { ms: Date.UTC(1962, 1, 5), year: "1962", label: "seven bodies within 16° — plus a total eclipse" },
  { ms: Date.UTC(1982, 2, 10), year: "1982", label: "syzygy — every planet one side of the sun" },
  { ms: Date.UTC(2000, 4, 5), year: "2000", label: "five-planet huddle behind the sun" },
  { ms: Date.UTC(2020, 11, 21), year: "2020", label: "great conjunction — jupiter meets saturn" },
  { ms: Date.UTC(2024, 3, 8), year: "2024", label: "total solar eclipse — sun · moon · earth" },
  { ms: Date.UTC(2040, 8, 8), year: "2040", label: "five bright planets gather at dusk" },
  { ms: Date.UTC(2080, 2, 15), year: "2080", label: "jupiter grazes saturn — closest pass for centuries" },
  { ms: Date.UTC(2161, 4, 19), year: "2161", label: "grand gathering — eight planets in a fan" },
  { ms: Date.UTC(2492, 4, 6), year: "2492", label: "the next true grand alignment" },
];

const mono = "font-[family-name:var(--font-jetbrains-mono)]";

/**
 * /sys HUD shell. The scene owns the 3D; this owns focus + time-speed
 * state and the telemetry chrome. The info card's numbers are REAL —
 * each planet's distance from the Sun for today's date, computed
 * client-side from the same ephemeris the scene renders.
 */
const SystemExperience = () => {
  const [focus, setFocus] = useState<string | null>(null);
  const [speed, setSpeed] = useState<SimSpeed>(0);
  const [jump, setJump] = useState<SimJump>(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [eventLabel, setEventLabel] = useState<string | null>(null);
  const [states, setStates] = useState<PlanetState[] | null>(null);
  const simDateRef = useRef<HTMLSpanElement>(null);

  const goToEvent = (ev: (typeof EVENTS)[number]) => {
    setJump({ ms: ev.ms, n: (jump?.n ?? 0) + 1 });
    setSpeed(-1); // hold at the moment so the geometry can be studied
    setEventLabel(ev.label);
    setEventsOpen(false);
    setFocus(null); // pull back to the overview — alignments read system-wide
  };

  const pickSpeed = (v: SimSpeed) => {
    setSpeed(v);
    if (v === 0) setEventLabel(null); // "now" returns home
  };

  // Arriving from a hero planet click (?focus=jupiter) → start the
  // fly-in immediately. window.location keeps the page fully static
  // (useSearchParams would force a Suspense boundary).
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("focus");
    if (wanted && BODIES.includes(wanted as (typeof BODIES)[number]) && wanted !== "sun") {
      // next frame: keeps hydration of the prerendered HUD clean and
      // satisfies react-hooks/set-state-in-effect
      const id = requestAnimationFrame(() => setFocus(wanted));
      return () => cancelAnimationFrame(id);
    }
  }, []);

  // Ephemeris values for the info card — client-only so the static build
  // never bakes a stale date.
  useEffect(() => {
    let alive = true;
    void import("../solar/ephemeris").then(({ getPlanetStates }) => {
      if (alive) setStates(getPlanetStates(new Date()));
    });
    if (simDateRef.current) {
      simDateRef.current.textContent = new Date().toLocaleDateString("en-CA");
    }
    return () => {
      alive = false;
    };
  }, []);

  const focusedState = focus ? states?.find((p) => p.name === focus) : undefined;
  const focusedMoons = focus ? MOONS.filter((m) => m.planet === focus) : [];

  return (
    <main className="relative h-dvh overflow-hidden bg-[#070710]">
      <SystemScene focus={focus} speed={speed} jump={jump} onFocus={setFocus} simDateRef={simDateRef} />

      {/* vignette only — the hero's CRT scanlines striped the Sun and
          dulled the whole scene out here; /sys is a window, not a CRT */}
      <div className="hero-vignette pointer-events-none absolute inset-0 z-10" />

      {/* ── top bar ── */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex items-start justify-between p-5">
        <div className={`${mono} text-[10px] uppercase tracking-widest`}>
          <p className="text-emerald-300/70">mdg::sys</p>
          <p className="mt-1 text-white/35">
            ephemeris <span ref={simDateRef} />
          </p>
          {eventLabel && <p className="mt-1 max-w-56 text-amber-200/70">{eventLabel}</p>}
        </div>
        <Link
          href="/"
          className={`${mono} pointer-events-auto rounded-md border border-white/10 bg-white/5 px-4 py-2 text-[10px] uppercase tracking-widest text-white/60 backdrop-blur-sm transition-all hover:border-white/30 hover:text-white`}
        >
          ← ./home
        </Link>
      </div>

      {/* ── info card (when a planet is focused) ── */}
      {focusedState && (
        <div className="pointer-events-none absolute left-5 top-24 z-30 w-60 rounded-lg border border-white/10 bg-black/45 p-4 backdrop-blur-md">
          <p className={`${mono} text-sm uppercase tracking-[0.3em] text-emerald-300/90`}>
            {focusedState.name}
          </p>
          <dl className={`${mono} mt-3 space-y-2 text-[10px] uppercase tracking-wider text-white/45`}>
            <div>
              <dt className="text-white/30">dist from sun · today</dt>
              <dd className="mt-0.5 text-emerald-200/80">
                {focusedState.rAU.toFixed(3)} au · {(focusedState.rAU * 149.6).toFixed(0)}m km
              </dd>
            </div>
            <div>
              <dt className="text-white/30">orbital period</dt>
              <dd className="mt-0.5 text-white/70">
                {focusedState.periodDays < 1000
                  ? `${focusedState.periodDays} days`
                  : `${(focusedState.periodDays / 365.25).toFixed(1)} years`}
              </dd>
            </div>
            {focusedMoons.length > 0 && (
              <div>
                <dt className="text-white/30">major moons</dt>
                <dd className="mt-0.5 space-y-0.5">
                  {focusedMoons.map((m) => (
                    <p key={m.name} className="text-white/70">
                      {m.name}
                      <span
                        className={
                          m.fidelity === "ephemeris" ? "text-emerald-400/80" : "text-white/25"
                        }
                      >
                        {" "}
                        {m.fidelity === "ephemeris" ? "● live" : "○ modeled"}
                      </span>
                      {m.direction === -1 && <span className="text-white/30"> · retrograde</span>}
                    </p>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* ── bottom chrome ── */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex flex-col items-center gap-3 p-5">
        <p className={`${mono} hidden text-[9px] uppercase tracking-[0.25em] text-white/25 sm:block`}>
          drag to orbit · scroll to zoom · click a planet
        </p>

        {/* alignments panel — real dates, real geometry */}
        {eventsOpen && (
          <div className="pointer-events-auto max-h-64 w-96 max-w-full overflow-y-auto rounded-lg border border-white/10 bg-black/60 p-2 backdrop-blur-md">
            {EVENTS.map((ev) => (
              <button
                key={ev.ms}
                onClick={() => goToEvent(ev)}
                className={`${mono} flex w-full items-baseline gap-3 rounded px-2.5 py-2 text-left text-[10px] uppercase tracking-wider text-white/60 transition-colors hover:bg-white/5 hover:text-white`}
              >
                <span className="shrink-0 text-amber-300/80">{ev.year}</span>
                <span>{ev.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* time machine */}
        <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-1.5">
          <span className={`${mono} mr-1 text-[9px] uppercase tracking-widest text-white/30`}>
            time
          </span>
          {SPEEDS.map((s) => (
            <button
              key={s.label}
              onClick={() => pickSpeed(s.value)}
              className={`${mono} rounded border px-2.5 py-1 text-[9px] uppercase tracking-widest transition-all ${
                speed === s.value
                  ? "border-amber-400/60 bg-amber-950/40 text-amber-200"
                  : "border-white/10 bg-white/[0.03] text-white/40 hover:border-white/25 hover:text-white/70"
              }`}
            >
              {s.label}
            </button>
          ))}
          <button
            onClick={() => setEventsOpen((o) => !o)}
            className={`${mono} rounded border px-2.5 py-1 text-[9px] uppercase tracking-widest transition-all ${
              eventsOpen
                ? "border-emerald-400/60 bg-emerald-950/40 text-emerald-200"
                : "border-emerald-500/25 bg-emerald-950/20 text-emerald-300/60 hover:border-emerald-400/50 hover:text-emerald-200"
            }`}
          >
            alignments
          </button>
        </div>

        {/* body rail */}
        <div className="pointer-events-auto -mx-5 flex max-w-full gap-1.5 overflow-x-auto px-5 pb-1">
          {BODIES.map((b) => {
            const active = b === "sun" ? focus === null : focus === b;
            return (
              <button
                key={b}
                onClick={() => setFocus(b === "sun" ? null : b)}
                className={`${mono} shrink-0 rounded border px-2.5 py-1.5 text-[10px] uppercase tracking-widest transition-all ${
                  active
                    ? "border-emerald-400/60 bg-emerald-950/40 text-emerald-200"
                    : "border-white/10 bg-white/[0.03] text-white/45 hover:border-white/25 hover:text-white/75"
                }`}
              >
                {b}
              </button>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default SystemExperience;
